import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchDoctors,
  fetchReports,
  addDoctor,
  deleteDoctor,
  updateDoctor,
  moderateReport,
  importDoctorsCsv,
} from "../lib/api";

const PAGE_SIZE = 10;

const defaultChecklist = [
  { key: "providers", label: "Provider data reviewed", checked: false },
  { key: "filters", label: "Map and filters tested", checked: false },
  { key: "cards", label: "Doctor cards verified", checked: false },
  { key: "reports", label: "Community reports working", checked: false },
  { key: "disclaimer", label: "Disclaimer visible everywhere", checked: false },
  { key: "mobile", label: "Mobile responsiveness tested", checked: false },
  { key: "admin", label: "Admin login secured", checked: false },
  { key: "faq", label: "FAQ page verified", checked: false },
  { key: "support", label: "Support email working", checked: false },
  { key: "erwait", label: "ER wait time marked as coming soon", checked: false },
  { key: "fake", label: "No fake availability claims", checked: false },
  { key: "launch", label: "Final launch approval completed", checked: false },
];

const emptyForm = {
  name: "",
  provider_type: "family",
  specialty: "Family Medicine",
  clinic_name: "",
  address: "",
  city: "",
  province: "Ontario",
  postal_code: "",
  phone: "",
  website: "",
  languages: "",
  status: "unknown",
  confidence_score: 60,
  latitude: "",
  longitude: "",
  last_verified: "",
  source_name: "",
  source_url: "",
  verification_method: "public_source",
  admin_verified: 0,
  notes: "",
  verification_notes: "",
};

const providerTypeLabels = {
  family: "Family Doctor",
  walkin: "Walk-in Clinic",
  primary: "Primary Care",
  pediatrics: "Pediatrics",
};

export default function Admin() {
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const [launchChecklist, setLaunchChecklist] = useState(() => {
    const saved = localStorage.getItem("pinmydoc-launch-checklist");
    return saved ? JSON.parse(saved) : defaultChecklist;
  });

  const toggleChecklistItem = (key) => {
    setLaunchChecklist((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const saveChecklist = () => {
    localStorage.setItem(
      "pinmydoc-launch-checklist",
      JSON.stringify(launchChecklist)
    );
    alert("Checklist saved successfully");
  };

  const loadDoctors = () => {
    return fetchDoctors({ limit: 500 }).then((data) => {
      setDoctors(data.doctors || []);
      setTotal(data.total || 0);
    });
  };

  const loadReports = () => {
    return fetchReports()
      .then((data) => setReports(data.reports || []))
      .catch((err) => console.error(err));
  };

  const refreshAll = () => {
    setLoading(true);
    Promise.all([loadDoctors(), loadReports()]).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openAddForm = () => {
    setEditingDoctor(null);
    setForm({
      ...emptyForm,
      last_verified: new Date().toISOString().slice(0, 10),
    });
    setShowForm(true);
  };

  const openEditForm = (doctor) => {
    setEditingDoctor(doctor);

    setForm({
      name: doctor.name || "",
      provider_type: doctor.provider_type || "family",
      specialty: doctor.specialty || "",
      clinic_name: doctor.clinic_name || "",
      address: doctor.address || "",
      city: doctor.city || "",
      province: doctor.province || "Ontario",
      postal_code: doctor.postal_code || "",
      phone: doctor.phone || "",
      website: doctor.website || "",
      languages: Array.isArray(doctor.languages)
        ? doctor.languages.join(", ")
        : doctor.languages || "",
      status: doctor.status || "unknown",
      confidence_score: Number(doctor.confidence_score || 60),
      latitude: doctor.latitude || "",
      longitude: doctor.longitude || "",
      last_verified: doctor.last_verified || doctor.last_updated || "",
      source_name: doctor.source_name || doctor.source || "",
      source_url: doctor.source_url || "",
      verification_method: doctor.verification_method || "public_source",
      admin_verified: Number(doctor.admin_verified || 0),
      notes: doctor.notes || "",
      verification_notes: doctor.verification_notes || "",
    });

    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDoctor(null);
    setForm(emptyForm);
  };

  const handleSaveDoctor = async () => {
    try {
      const payload = {
        ...form,
        confidence_score: Number(form.confidence_score || 60),
        admin_verified: Number(form.admin_verified || 0),
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };

      const data = editingDoctor
        ? await updateDoctor({ ...payload, id: editingDoctor.id })
        : await addDoctor(payload);

      if (data.success) {
        alert(editingDoctor ? "Provider updated." : "Provider added.");
        closeForm();
        refreshAll();
      } else {
        alert(data.detail || "Save failed.");
      }
    } catch (error) {
      alert(error.message || "Server error");
    }
  };

  const handleDeleteDoctor = async (doctor) => {
    if (!window.confirm(`Delete ${doctor.name}?`)) return;

    try {
      const data = await deleteDoctor(doctor.id);

      if (data.success) {
        alert("Provider deleted.");
        refreshAll();
      } else {
        alert(data.detail || "Delete failed.");
      }
    } catch (error) {
      alert(error.message || "Server error");
    }
  };

  const handleModerateReport = async (report, action) => {
    try {
      const data = await moderateReport(report.id, action);

      if (data.success) {
        alert(`Report ${action}d.`);
        refreshAll();
      } else {
        alert(data.detail || "Moderation failed.");
      }
    } catch (error) {
      alert(error.message || "Server error");
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await importDoctorsCsv(file);
      alert(
        `CSV Import Complete\n\nInserted: ${data.inserted}\nSkipped: ${data.skipped}`
      );
      refreshAll();
    } catch (error) {
      alert(error.message || "CSV import failed");
    }

    event.target.value = "";
  };

  const cities = useMemo(() => {
    return [...new Set(doctors.map((d) => d.city).filter(Boolean))].sort();
  }, [doctors]);

  const pendingReports = reports.filter(
    (r) => Number(r.moderated) === 0 && Number(r.hidden) === 0
  );

  const recentlyVerified = useMemo(() => {
    return doctors.filter((d) => {
      const date = d.last_verified || d.last_updated;
      if (!date) return false;
      const diff = Date.now() - new Date(date).getTime();
      return diff < 1000 * 60 * 60 * 24 * 7;
    });
  }, [doctors]);

  const acceptingCount = doctors.filter((d) => d.status === "accepting").length;

  const filteredDoctors = useMemo(() => {
    const q = search.toLowerCase().trim();

    return doctors.filter((d) => {
      const matchesSearch =
        !q ||
        `${d.name || ""} ${d.clinic_name || ""} ${d.city || ""}`
          .toLowerCase()
          .includes(q);

      const matchesType = !typeFilter || d.provider_type === typeFilter;
      const matchesCity = !cityFilter || d.city === cityFilter;
      const matchesStatus = !statusFilter || d.status === statusFilter;

      return matchesSearch && matchesType && matchesCity && matchesStatus;
    });
  }, [doctors, search, typeFilter, cityFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredDoctors.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedDoctors = filteredDoctors.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Admin Dashboard
          </h1>

          <p className="mt-3 text-slate-600 text-lg">
            Manage providers, reports, verification, analytics, and launch readiness.
          </p>
        </div>

        <button
          onClick={() => {
            localStorage.removeItem("admin-auth");
            navigate("/admin-login");
          }}
          className="bg-red-500 hover:bg-red-600 text-white px-5 py-3 rounded-xl font-semibold"
        >
          Logout
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-5 mb-10">
        <div className="rounded-[2rem] bg-gradient-to-br from-[#EEF5FB] to-[#DCEEFF] border border-[#D7E7F6] p-6 shadow-lg">
          <div className="text-sm font-semibold text-slate-500">
            Total Providers
          </div>
          <div className="mt-3 text-4xl font-black text-[#1E4256]">
            {total}
          </div>
        </div>

        <div className="rounded-[2rem] bg-gradient-to-br from-[#ECFDF3] to-[#DDF7EA] border border-[#CDEBDD] p-6 shadow-lg">
          <div className="text-sm font-semibold text-slate-500">
            Accepting Patients
          </div>
          <div className="mt-3 text-4xl font-black text-[#18794E]">
            {acceptingCount}
          </div>
        </div>

        <div className="rounded-[2rem] bg-gradient-to-br from-[#FFF8E7] to-[#FFF2CC] border border-[#F4DE9B] p-6 shadow-lg">
          <div className="text-sm font-semibold text-slate-500">
            Pending Reports
          </div>
          <div className="mt-3 text-4xl font-black text-[#B7791F]">
            {pendingReports.length}
          </div>
        </div>

        <div className="rounded-[2rem] bg-gradient-to-br from-[#F4F1FF] to-[#E9E2FF] border border-[#DDD0FF] p-6 shadow-lg">
          <div className="text-sm font-semibold text-slate-500">
            Cities Covered
          </div>
          <div className="mt-3 text-4xl font-black text-[#6941C6]">
            {cities.length}
          </div>
        </div>

        <div className="rounded-[2rem] bg-gradient-to-br from-[#FFF1F3] to-[#FFE4E8] border border-[#FFD1DA] p-6 shadow-lg">
          <div className="text-sm font-semibold text-slate-500">
            Recently Verified
          </div>
          <div className="mt-3 text-4xl font-black text-[#BE185D]">
            {recentlyVerified.length}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="bg-white border border-slate-200 rounded-[2rem] shadow-lg p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                Launch Readiness Checklist
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Internal PinMyDoc launch preparation checklist.
              </p>
            </div>

            <button
              onClick={saveChecklist}
              className="px-5 py-3 rounded-2xl bg-[#356DFF] text-white font-semibold shadow-lg"
            >
              Save Checklist
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {launchChecklist.map((item) => (
              <label
                key={item.key}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={() => toggleChecklistItem(item.key)}
                  className="w-5 h-5 accent-[#356DFF]"
                />
                <span className="text-sm font-medium text-slate-700">
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#EEF5FB] to-[#DCEEFF] border border-[#D7E7F6] rounded-[2rem] p-6 shadow-lg">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                Analytics Dashboard
              </h2>
              <p className="mt-2 text-sm text-slate-600 leading-6">
                Open analytics tools to monitor visitors, devices, heatmaps,
                recordings, and launch activity.
              </p>
            </div>

            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm text-2xl shrink-0">
              📊
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <a
              href="https://analytics.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl bg-white border border-slate-200 px-4 py-4 text-center hover:shadow-md transition-all"
            >
              <div className="text-2xl mb-2">📈</div>
              <div className="text-sm font-bold text-slate-800">
                Google Analytics
              </div>
            </a>

            <a
              href="https://clarity.microsoft.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl bg-white border border-slate-200 px-4 py-4 text-center hover:shadow-md transition-all"
            >
              <div className="text-2xl mb-2">🔥</div>
              <div className="text-sm font-bold text-slate-800">
                Microsoft Clarity
              </div>
            </a>

            <a
              href="https://analytics.google.com/analytics/web/#/realtime"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl bg-white border border-slate-200 px-4 py-4 text-center hover:shadow-md transition-all"
            >
              <div className="text-2xl mb-2">🟢</div>
              <div className="text-sm font-bold text-slate-800">
                Realtime Users
              </div>
            </a>

            <a
              href="https://clarity.microsoft.com/projects/view/wuskt4sba8/recordings"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl bg-white border border-slate-200 px-4 py-4 text-center hover:shadow-md transition-all"
            >
              <div className="text-2xl mb-2">🎥</div>
              <div className="text-sm font-bold text-slate-800">
                Session Recordings
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900">
            CSV Real Data Import
          </h2>

          <p className="mt-2 text-sm text-slate-600 leading-6">
            Import verified provider data using approved CSV format.
          </p>

          <label className="mt-4 inline-block bg-[#2C5E7A] hover:bg-[#1E4256] text-white px-4 py-2 rounded-xl font-semibold cursor-pointer">
            Upload CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvImport}
              className="hidden"
            />
          </label>
        </div>

        <div className="bg-[#F5FAFF] border border-[#D7E7F6] rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900">
            Platform Rule
          </h2>
          <p className="mt-2 text-sm text-slate-600 leading-6">
            Always instruct users to call clinics directly. Never guarantee live
            availability or medical advice.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-10">
        <div className="p-6 border-b border-slate-200 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-2xl font-bold text-slate-900">
              Provider Records
            </h2>

            <button
              onClick={openAddForm}
              className="bg-[#2C5E7A] hover:bg-[#1E4256] text-white px-4 py-2 rounded-xl font-semibold"
            >
              Add Provider
            </button>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <input
              className="border rounded-xl px-4 py-3"
              placeholder="Search provider or clinic"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />

            <select
              className="border rounded-xl px-4 py-3"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Types</option>
              <option value="family">Family Doctor</option>
              <option value="walkin">Walk-in Clinic</option>
              <option value="primary">Primary Care</option>
              <option value="pediatrics">Pediatrics</option>
            </select>

            <select
              className="border rounded-xl px-4 py-3"
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            <select
              className="border rounded-xl px-4 py-3"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="accepting">Accepting</option>
              <option value="likely_accepting">Likely Accepting</option>
              <option value="unknown">Call to Confirm</option>
              <option value="not_accepting">Not Accepting</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Clinic</th>
                <th className="text-left p-4">City</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Confidence</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {pagedDoctors.map((doctor) => (
                <tr key={doctor.id} className="border-t border-slate-100">
                  <td className="p-4 font-semibold">{doctor.name}</td>
                  <td className="p-4">
                    {providerTypeLabels[doctor.provider_type] || doctor.provider_type}
                  </td>
                  <td className="p-4">{doctor.clinic_name}</td>
                  <td className="p-4">{doctor.city}</td>
                  <td className="p-4">{doctor.status}</td>
                  <td className="p-4">{doctor.confidence_score || 0}%</td>
                  <td className="p-4 whitespace-nowrap">
                    <button
                      onClick={() => openEditForm(doctor)}
                      className="text-[#2C5E7A] font-semibold mr-4"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDeleteDoctor(doctor)}
                      className="text-red-500 font-semibold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {pagedDoctors.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-slate-500">
                    No matching provider records.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-5 border-t flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            Showing {pagedDoctors.length} of {filteredDoctors.length} filtered
            records
          </p>

          <div className="flex gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-xl border disabled:opacity-40"
            >
              Previous
            </button>

            <span className="px-4 py-2 text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-4 py-2 rounded-xl border disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            Community Reports
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-4">Doctor</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Note</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {reports.map((report) => {
                const isHidden = Number(report.hidden) === 1;
                const isModerated = Number(report.moderated) === 1;

                return (
                  <tr key={report.id} className="border-t border-slate-100">
                    <td className="p-4 font-semibold">{report.doctor_name}</td>
                    <td className="p-4">{report.report_type}</td>
                    <td className="p-4">{report.note || "-"}</td>
                    <td className="p-4">
                      {isHidden ? "Hidden" : isModerated ? "Approved" : "Pending"}
                    </td>

                    <td className="p-4 whitespace-nowrap">
                      {!isModerated && !isHidden ? (
                        <>
                          <button
                            onClick={() =>
                              handleModerateReport(report, "approve")
                            }
                            className="text-green-600 font-semibold mr-3"
                          >
                            Approve
                          </button>

                          <button
                            onClick={() =>
                              handleModerateReport(report, "reject")
                            }
                            className="text-orange-600 font-semibold"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="text-slate-400">Completed</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {reports.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-6 text-center text-slate-500">
                    No reports yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">
                  {editingDoctor ? "Edit Provider" : "Add Provider"}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Add full provider details, verification information, and map coordinates.
                </p>
              </div>

              <button onClick={closeForm} className="text-slate-500 text-2xl">
                ×
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <input className="border rounded-xl px-4 py-3" placeholder="Provider Name" value={form.name} onChange={(e) => updateForm("name", e.target.value)} />

              <select className="border rounded-xl px-4 py-3" value={form.provider_type} onChange={(e) => updateForm("provider_type", e.target.value)}>
                <option value="family">Family Doctor</option>
                <option value="walkin">Walk-in Clinic</option>
                <option value="primary">Primary Care</option>
                <option value="pediatrics">Pediatrics</option>
              </select>

              <input className="border rounded-xl px-4 py-3" placeholder="Specialty" value={form.specialty} onChange={(e) => updateForm("specialty", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Clinic Name" value={form.clinic_name} onChange={(e) => updateForm("clinic_name", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Address" value={form.address} onChange={(e) => updateForm("address", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="City" value={form.city} onChange={(e) => updateForm("city", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Province" value={form.province} onChange={(e) => updateForm("province", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Postal Code" value={form.postal_code} onChange={(e) => updateForm("postal_code", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Phone" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Website" value={form.website} onChange={(e) => updateForm("website", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Languages e.g. English, French" value={form.languages} onChange={(e) => updateForm("languages", e.target.value)} />

              <select className="border rounded-xl px-4 py-3" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                <option value="unknown">Call to Confirm</option>
                <option value="accepting">Accepting Patients</option>
                <option value="likely_accepting">Likely Accepting</option>
                <option value="not_accepting">Not Accepting</option>
              </select>

              <input className="border rounded-xl px-4 py-3" type="number" min="0" max="100" placeholder="Confidence Score" value={form.confidence_score} onChange={(e) => updateForm("confidence_score", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" type="date" placeholder="Last Verified" value={String(form.last_verified || "").slice(0, 10)} onChange={(e) => updateForm("last_verified", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Latitude" value={form.latitude} onChange={(e) => updateForm("latitude", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Longitude" value={form.longitude} onChange={(e) => updateForm("longitude", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Source Name" value={form.source_name} onChange={(e) => updateForm("source_name", e.target.value)} />

              <input className="border rounded-xl px-4 py-3" placeholder="Source URL" value={form.source_url} onChange={(e) => updateForm("source_url", e.target.value)} />

              <select className="border rounded-xl px-4 py-3" value={form.verification_method} onChange={(e) => updateForm("verification_method", e.target.value)}>
                <option value="public_source">Public Source</option>
                <option value="clinic_website">Clinic Website</option>
                <option value="phone_verified">Phone Verified</option>
                <option value="admin_review">Admin Review</option>
                <option value="community_report">Community Report</option>
              </select>

              <select className="border rounded-xl px-4 py-3" value={form.admin_verified} onChange={(e) => updateForm("admin_verified", e.target.value)}>
                <option value={0}>Not Admin Verified</option>
                <option value={1}>Admin Verified</option>
              </select>

              <textarea className="border rounded-xl px-4 py-3 md:col-span-2" rows="3" placeholder="Notes" value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />

              <textarea className="border rounded-xl px-4 py-3 md:col-span-2" rows="3" placeholder="Verification Notes" value={form.verification_notes} onChange={(e) => updateForm("verification_notes", e.target.value)} />
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={closeForm}
                className="px-5 py-3 rounded-xl border border-slate-300 font-semibold text-slate-700"
              >
                Cancel
              </button>

              <button
                onClick={handleSaveDoctor}
                className="px-5 py-3 rounded-xl bg-[#2C5E7A] text-white font-semibold"
              >
                {editingDoctor ? "Update Provider" : "Save Provider"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}